import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, ExternalLink } from 'lucide-react';
import { Contract } from '../../types/contract';
import ContractStatusPill from './ContractStatusPill';
import { useNotifications } from '../../contexts/NotificationContext';

interface ContractSummaryPanelProps {
  contract: Contract;
}

const ContractSummaryPanel: React.FC<ContractSummaryPanelProps> = ({ contract }) => {
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const { showNotification } = useNotifications();

  // Calculate mapping progress percentage
  const mappingProgress = contract.data?.mappingProgress || { mapped: 0, total: 0, required: 0, requiredMapped: 0 };
  const mappingProgressPercentage = mappingProgress.total > 0
    ? Math.round((mappingProgress.mapped / mappingProgress.total) * 100)
    : 0;

  // Handle copy to clipboard
  const handleCopyToClipboard = () => {
    if (!contract.share_token) return;

    const shareLink = `${window.location.origin}/contracts/share/${contract.share_token}`;

    navigator.clipboard.writeText(shareLink)
      .then(() => {
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
        showNotification('success', 'Share link copied to clipboard.');
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        showNotification('error', 'Failed to copy to clipboard.');
      });
  };

  return (
    <div className="space-y-6">
      {/* Contract Status */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Contract Status</h3>
        {contract.status && <ContractStatusPill status={contract.status as 'accepted' | 'pending' | 'draft'} />}
      </div>

      {/* Mapping Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Mapping Progress Summary</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="mb-2">
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {mappingProgress.mapped} of {mappingProgress.total} properties mapped
              </span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {mappingProgressPercentage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${mappingProgressPercentage}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-3">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                Required: {mappingProgress.requiredMapped} / {mappingProgress.required}
              </p>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-md p-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                Optional: {mappingProgress.mapped - mappingProgress.requiredMapped} / {mappingProgress.total - mappingProgress.required}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Share Link */}
      {contract.share_token && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Share Link</h3>
          </div>

          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-md px-4 py-2 text-gray-900 dark:text-white font-mono text-sm truncate">
                {`${window.location.origin}/contracts/share/${contract.share_token}`}
              </div>
              <button
                type="button"
                onClick={handleCopyToClipboard}
                className="ml-2 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 relative"
              >
                <Copy className="h-5 w-5" />
                {showCopiedMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded"
                  >
                    Copied!
                  </motion.div>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (contract.share_token) {
                    const shareLink = `${window.location.origin}/contracts/share/${contract.share_token}`;
                    window.open(shareLink, '_blank');
                  }
                }}
                className="ml-2 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                title="Open in browser"
              >
                <ExternalLink className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractSummaryPanel;
